/**
 * Microfonul și difuzorul. Atât.
 *
 * Bucla — ce stare urmează, cui i s-a vorbit, ce se rostește din răspuns — stă
 * pe server (backend/voiceloop.py). Aici e doar ce nu poate fi altundeva:
 * capturarea sunetului, detecția de tăcere, rostirea, și oprirea rostirii când
 * omul vorbește peste ea.
 *
 * Regula pe care o respectă fișierul ăsta: nu decide nimic despre conversație.
 * Trimite sunet, primește stare, o arată. Prima versiune a acestui fișier ar fi
 * fost tentată să știe ce e cuvântul de trezire; a doua, pe Android, ar fi știut
 * altceva.
 */

/** Câtă energie sonoră înseamnă „cineva vorbește". Zgomotul de fond al unui
 *  birou stă sub 0.01; o voce normală trece de 0.05. */
const PRAG_VOCE = 0.035;

/** Câte cadre consecutive peste prag înseamnă vorbire, nu o ușă trântită. */
const CADRE_VOCE = 3;

function rms(buf) {
  let s = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i] - 128) / 128;
    s += v * v;
  }
  return Math.sqrt(s / buf.length);
}

/**
 * Ascultă până la tăcere și întoarce sunetul.
 *
 * `onNivel` primește energia curentă la fiecare cadru, ca interfața să poată
 * arăta că microfonul chiar aude ceva — un microfon care pare deschis dar nu
 * aude e cel mai enervant fel de defect.
 */
export function asculta(stream, { tacereMs, maxSecunde, onNivel, semnal }) {
  return new Promise((resolve, reject) => {
    let recorder;
    try {
      recorder = new MediaRecorder(stream);
    } catch (e) {
      reject(e);
      return;
    }
    const bucati = [];
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const sursa = ctx.createMediaStreamSource(stream);
    const analiza = ctx.createAnalyser();
    analiza.fftSize = 512;
    sursa.connect(analiza);
    const buf = new Uint8Array(analiza.frequencyBinCount);

    let aVorbit = false;
    let tacereDeLa = Date.now();
    let oprit = false;

    const gata = () => {
      if (oprit) return;
      oprit = true;
      try { recorder.stop(); } catch { /* deja oprit */ }
    };

    const cadru = () => {
      if (oprit) return;
      analiza.getByteTimeDomainData(buf);
      const nivel = rms(buf);
      if (onNivel) onNivel(nivel);
      if (nivel > PRAG_VOCE) {
        aVorbit = true;
        tacereDeLa = Date.now();
      } else if (aVorbit && Date.now() - tacereDeLa > tacereMs) {
        // Tăcere după ce s-a vorbit: replica s-a terminat.
        gata();
        return;
      }
      requestAnimationFrame(cadru);
    };

    const limita = setTimeout(gata, maxSecunde * 1000);
    if (semnal) semnal.addEventListener("abort", gata, { once: true });

    recorder.ondataavailable = (e) => e.data.size && bucati.push(e.data);
    recorder.onstop = () => {
      clearTimeout(limita);
      ctx.close().catch(() => {});
      // Fără vorbire deloc = fără sunet de trimis. Altfel fiecare pauză ar
      // costa o transcriere și un răspuns la nimic.
      resolve(aVorbit ? new Blob(bucati, { type: recorder.mimeType || "audio/webm" }) : null);
    };
    recorder.start();
    requestAnimationFrame(cadru);
  });
}

/**
 * Rostește, și se oprește dacă omul vorbește peste ea.
 *
 * `onIntrerupt` e chemat o singură dată, când vocea omului trece pragul destule
 * cadre la rând. Asta e barge-in-ul: fără el, un răspuns lung nu se poate opri
 * decât închizând aplicația.
 */
export function rosteste(text, stream, { barge_in, onIntrerupt }) {
  return new Promise((resolve) => {
    if (!text || typeof window.speechSynthesis === "undefined") {
      resolve("gata");
      return;
    }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "ro-RO";
    let terminat = false;
    let ctx = null;
    let cadre = 0;

    const inchide = (cum) => {
      if (terminat) return;
      terminat = true;
      if (ctx) ctx.close().catch(() => {});
      resolve(cum);
    };

    u.onend = () => inchide("gata");
    u.onerror = () => inchide("gata");

    if (barge_in && stream) {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analiza = ctx.createAnalyser();
      analiza.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analiza);
      const buf = new Uint8Array(analiza.frequencyBinCount);
      const cadru = () => {
        if (terminat) return;
        analiza.getByteTimeDomainData(buf);
        // Se cer mai multe cadre la rând: difuzorul propriu, o ușă, o tuse — nu
        // sunt o întrerupere. Un barge-in fals oprește Atena în mijlocul unei
        // propoziții și pare defect.
        cadre = rms(buf) > PRAG_VOCE ? cadre + 1 : 0;
        if (cadre >= CADRE_VOCE) {
          window.speechSynthesis.cancel();
          if (onIntrerupt) onIntrerupt();
          inchide("intrerupt");
          return;
        }
        requestAnimationFrame(cadru);
      };
      requestAnimationFrame(cadru);
    }

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  });
}

export const PRAGURI = { PRAG_VOCE, CADRE_VOCE };
